import * as React from "react";
import * as S from "@styled";
import {
    ChangeStepProps,
    TemplateWizardStep,
} from "../../../pages/integrationCatalog/TemplateWizard";
import { Parameter } from "model/Parameter";
import Button, { SaveButton } from "components/shared/Button";
import Text from "components/shared/Text";
import Input from "components/shared/Input";
import { UseMutationResult } from "react-query";
import { DeployedProcess } from "model/process/DeployedProcess";
import useDeferredMutation from "hooks/useDeferredMutation";
import { ProcessInstanceBase } from "model/process/ProcessInstance";
import Tooltip from "components/shared/Tooltip";
import RunDetailsTable from "../RunDetailsTable";

const TestIntegrationStep: React.FC<TestIntegrationProps> = (props) => {
    const manualTriggerParametersElements = props.manualTriggerParameters?.map(
        (parameter) => {
            return (
                <React.Fragment key={parameter.name}>
                    <Input
                        label={parameter.name}
                        value={parameter.nonStringifiedDefaultValue || ""}
                        onChange={(e: string) =>
                            props.onChangeManualTriggerParameter(
                                parameter.name,
                                e,
                            )
                        }
                    />
                    {parameter.description && (
                        <Text tag="p">{parameter.description}</Text>
                    )}
                </React.Fragment>
            );
        },
    );

    const deployedTestProcess = props.runTestExecutionMutation.data;

    const [processInstances, setProcessInstances] =
        React.useState<ProcessInstanceBase[]>();

    const instancesGET = useDeferredMutation<ProcessInstanceBase[]>(
        "GET",
        undefined,
        "Failed to fetch available process trial run instances.",
    );

    const fetchInstances = () => {
        if (deployedTestProcess?.processGuid !== undefined) {
            instancesGET.reset();
            instancesGET.mutate({
                url: `processes/trial-run-instances?processGuid=${deployedTestProcess.processGuid}`,
            });
        }
    };

    const fetchInstancesUsingTimeout = (timeout: number) => {
        if (deployedTestProcess?.processGuid !== undefined) {
            setTimeout(() => {
                fetchInstances();
            }, timeout);
        }
    };

    React.useEffect(() => {
        // give results little bit time to land on frends tenant's database
        fetchInstancesUsingTimeout(100);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deployedTestProcess?.processGuid]);

    React.useEffect(() => {
        // on error, refetch until results are found from frends tenant's database
        if (instancesGET.isError) {
            fetchInstancesUsingTimeout(500);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [instancesGET.isError]);

    React.useEffect(() => {
        if (instancesGET.data) {
            setProcessInstances(instancesGET.data);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [instancesGET.data]);

    const instancesGETPolling = instancesGET.isLoading || instancesGET.isError;

    return (
        <S.TemplateWizardPageContainer>
            {manualTriggerParametersElements}

            <S.TemplateWizardExecuteButtonContainer>
                <SaveButton
                    variant="bordered"
                    onClick={props.onRunTrialExecution}
                    disabled={!props.hasManualTrigger}
                    loading={props.runTestExecutionMutation.isLoading}
                    success={props.runTestExecutionMutation.isSuccess}
                    error={props.runTestExecutionMutation.isError}
                >
                    Execute trial run
                </SaveButton>
                <Tooltip placement="top">
                    Deploys the Process to the testing Agent group and executes
                    it.
                </Tooltip>
            </S.TemplateWizardExecuteButtonContainer>

            <RunDetailsTable
                processInstances={processInstances}
                isLoading={instancesGETPolling}
                onRefreshButtonClick={fetchInstances}
            />

            <S.TemplateWizardStepButtonsContainer>
                <Button
                    variant="bordered"
                    onClick={() =>
                        props.onChangeStep(
                            TemplateWizardStep.ConfigureVariables,
                        )
                    }
                >
                    Back
                </Button>
                <Button
                    variant="bordered"
                    onClick={() =>
                        props.onChangeStep(TemplateWizardStep.DeployIntegration)
                    }
                >
                    Continue
                </Button>
            </S.TemplateWizardStepButtonsContainer>
        </S.TemplateWizardPageContainer>
    );
};

interface TestIntegrationProps extends ChangeStepProps {
    hasManualTrigger: boolean;
    manualTriggerParameters?: Parameter[];
    runTestExecutionMutation: TestDeploymentMutationResult;
    onRunTrialExecution: () => void;
    onChangeManualTriggerParameter: (
        parameterName: string,
        value: string,
    ) => void;
}

export type TestDeploymentMutationResult = UseMutationResult<
    DeployedProcess,
    unknown,
    void | Record<string, unknown>,
    unknown
>;

export default TestIntegrationStep;
